import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Clock, CheckCircle, ChefHat, UtensilsCrossed, PartyPopper, XCircle,
  RefreshCw, Plus, Utensils, Receipt, Package, Star, Send,
  X, Download, Heart
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

interface OrderItem {
  foodItem: {
    _id: string;
    name: string;
    nameAr?: string;
    price: number;
    image?: string;
  };
  quantity: number;
  price: number;
  size?: {
    name: string;
    nameAr?: string;
    price: number;
  };
  specialInstructions: string;
}

interface TimelineEvent {
  status: string;
  timestamp: string;
  note: string;
}

interface ItemRating {
  foodItemId: string;
  rating: number;
  comment: string;
}

interface Order {
  _id: string;
  orderNumber: string;
  confirmationCode: string;
  status: string;
  total: number;
  estimatedDeliveryTime: string;
  createdAt: string;
  timeline: TimelineEvent[];
  restaurant: {
    _id: string;
    name: string;
    nameAr?: string;
    logo: string;
  };
  table: {
    _id: string;
    tableNumber: string;
    qrCode?: string;
  };
  items: OrderItem[];
  rating?: {
    food: number;
    service: number;
    comment: string;
    ratedAt: string;
  };
  itemRatings?: Array<{
    foodItem: string;
    rating: number;
    comment: string;
  }>;
}

interface ReceiptOrder {
  orderNumber: string;
  confirmationCode: string;
  items: Array<{
    name: string;
    nameAr?: string;
    quantity: number;
    price: number;
    total: number;
  }>;
  subtotal: number;
  tax: number;
  total: number;
  createdAt: string;
}

interface ReceiptData {
  restaurant: {
    name: string;
    nameAr?: string;
    logo: string;
    address: string | { street?: string; city?: string; state?: string; zipCode?: string; coordinates?: any };
    phone: string;
  };
  table: {
    tableNumber: string;
  };
  orders: ReceiptOrder[];
  summary: {
    subtotal: number;
    tax: number;
    total: number;
    orderCount: number;
  };
  closedAt: string;
}


const TableOrderStatusPage: React.FC = () => {
  const { confirmationCode } = useParams<{ confirmationCode: string }>();
  const [searchParams] = useSearchParams();
  const tableQrCode = searchParams.get('table');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, language, isRTL } = useLanguage();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Rating state
  const [showRating, setShowRating] = useState(false);
  const [overallRating, setOverallRating] = useState(0);
  const [serviceRating, setServiceRating] = useState(0);
  const [comment, setComment] = useState('');
  const [itemRatings, setItemRatings] = useState<ItemRating[]>([]);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [hasRated, setHasRated] = useState(false);

  // Close table state
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState<ReceiptData | null>(null);
  const [closingTable, setClosingTable] = useState(false);
  const [tableClosed, setTableClosed] = useState(false);
  const receiptRef = useRef<HTMLDivElement>(null);

  // Session ended state
  const [sessionEnded, setSessionEnded] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);

  // Check if session is still valid
  const checkSessionValidity = async () => {
    const qrCode = tableQrCode || order?.table?.qrCode;
    if (!qrCode) return true; // No QR code, can't check

    try {
      const sessionToken = localStorage.getItem(`table_session_${qrCode}`);
      const response = await apiClient.get(`/table/${qrCode}/session/check`, {
        headers: { 'x-table-session': sessionToken || '' }
      });

      if (!response.data.valid && response.data.sessionEnded) {
        setSessionEnded(true);
        // Clear local session
        localStorage.removeItem(`table_session_${qrCode}`);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error checking session:', error);
      return true; // On error, don't block the user
    }
  };

  const fetchOrderStatus = async (code: string) => {
    if (!code || code === 'undefined') {
      console.warn('Invalid confirmation code:', code);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get(`/table/orders/${code}/status`);
      setOrder(response.data.order);

      // Check if already rated
      if (response.data.order.rating?.ratedAt) {
        setHasRated(true);
      }

      // Initialize item ratings
      if (response.data.order.items) {
        setItemRatings(response.data.order.items.map((item: OrderItem) => ({
          foodItemId: item.foodItem._id,
          rating: 0,
          comment: '',
        })));
      }
    } catch (error) {
      console.error('Error fetching order status:', error);
      toast({
        title: t("error"),
        description: t("orderNotFound"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    const code = order?.confirmationCode || confirmationCode;
    if (!code) return;
    setRefreshing(true);
    await fetchOrderStatus(code);
    setRefreshing(false);
    toast({
      title: t("statusUpdated"),
      description: t("orderStatusRefreshed"),
    });
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualCode.trim()) {
      fetchOrderStatus(manualCode.trim());
    }
  };

  const handleOrderMore = () => {
    const qrCode = tableQrCode || order?.table?.qrCode;
    if (qrCode) {
      navigate(`/table/${qrCode}`);
    } else {
      toast({
        title: t("error"),
        description: t("unableToFindTable"),
        variant: "destructive",
      });
    }
  };

  const handleItemRatingChange = (foodItemId: string, rating: number) => {
    setItemRatings(prev => prev.map(ir =>
      ir.foodItemId === foodItemId ? { ...ir, rating } : ir
    ));
  };

  const handleSubmitRating = async () => {
    if (overallRating === 0) {
      toast({
        title: t("error"),
        description: t("pleaseProvideOverallRating"),
        variant: "destructive",
      });
      return;
    }

    setSubmittingRating(true);
    try {
      const code = order?.confirmationCode || confirmationCode;
      await apiClient.post(`/table/orders/${code}/rate`, {
        overallRating,
        serviceRating,
        comment,
        itemRatings: itemRatings.filter(ir => ir.rating > 0),
      });

      toast({
        title: t("success"),
        description: t("thankYouForRating"),
      });
      setHasRated(true);
      setShowRating(false);
    } catch (error: any) {
      console.error('Error submitting rating:', error);
      toast({
        title: t("error"),
        description: error.response?.data?.message || t("failedToSubmitRating"),
        variant: "destructive",
      });
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleCloseTable = async () => {
    const qrCode = tableQrCode || order?.table?.qrCode;
    if (!qrCode) {
      toast({
        title: t("error"),
        description: t("unableToFindTable"),
        variant: "destructive",
      });
      return;
    }

    setClosingTable(true);
    try {
      const sessionToken = localStorage.getItem(`table_session_${qrCode}`);
      const response = await apiClient.post(`/table/close/${qrCode}`, {}, {
        headers: { 'x-table-session': sessionToken || '' }
      });

      setReceiptData(response.data.receipt);
      setTableClosed(true);
      setShowReceipt(true);

      // Clear session token
      localStorage.removeItem(`table_session_${qrCode}`);

      toast({
        title: t("success"),
        description: t("tableClosedSuccessfully"),
      });
    } catch (error: any) {
      console.error('Error closing table:', error);
      toast({
        title: t("error"),
        description: error.response?.data?.message || t("failedToCloseTable"),
        variant: "destructive",
      });
    } finally {
      setClosingTable(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!receiptData || !receiptRef.current) return;

    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      // Capture the receipt content as canvas
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      // Calculate dimensions for receipt-style PDF (80mm width)
      const imgWidth = 80;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Create PDF with dynamic height
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [imgWidth, imgHeight + 10],
      });

      // Add the canvas as image
      const imgData = canvas.toDataURL('image/png');
      doc.addImage(imgData, 'PNG', 0, 5, imgWidth, imgHeight);

      // Save the PDF
      doc.save(`receipt-table-${receiptData.table.tableNumber}-${new Date().toISOString().split('T')[0]}.pdf`);

      toast({ title: t("success"), description: t("receiptDownloaded") });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: t("error"),
        description: t("failedToDownloadReceipt"),
        variant: "destructive",
      });
    }
  };

  // Check session validity on mount
  useEffect(() => {
    const checkSession = async () => {
      const isValid = await checkSessionValidity();
      if (!isValid) {
        setSessionEnded(true);
      }
    };

    if (tableQrCode) {
      checkSession();
    }
  }, [tableQrCode]);

  // Redirect countdown when session ends
  useEffect(() => {
    if (sessionEnded && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (sessionEnded && redirectCountdown === 0) {
      const qrCode = tableQrCode || order?.table?.qrCode;
      if (qrCode) {
        navigate(`/table/${qrCode}`);
      } else {
        navigate('/');
      }
    }
  }, [sessionEnded, redirectCountdown, tableQrCode, order?.table?.qrCode, navigate]);

  useEffect(() => {
    if (confirmationCode) {
      fetchOrderStatus(confirmationCode);
    }
  }, [confirmationCode]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const code = order?.confirmationCode || confirmationCode;
    if (code && order && order.status !== 'served' && order.status !== 'cancelled') {
      const interval = setInterval(() => {
        fetchOrderStatus(code);
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [order?.confirmationCode, order?.status, confirmationCode]);

  // Show rating prompt when order is served
  useEffect(() => {
    if (order?.status === 'served' && !hasRated && !order.rating?.ratedAt) {
      // Delay showing rating prompt
      const timer = setTimeout(() => {
        setShowRating(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [order?.status, hasRated, order?.rating?.ratedAt]);

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { color: string; bgColor: string; icon: React.ReactNode; label: string }> = {
      'placed': {
        color: 'text-blue-600',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        icon: <Clock className="w-5 h-5" />,
        label: t("orderPlaced")
      },
      'confirmed': {
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        icon: <CheckCircle className="w-5 h-5" />,
        label: t("confirmed")
      },
      'preparing': {
        color: 'text-orange-600',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        icon: <ChefHat className="w-5 h-5" />,
        label: t("preparing")
      },
      'ready': {
        color: 'text-green-600',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        icon: <UtensilsCrossed className="w-5 h-5" />,
        label: t("ready")
      },
      'served': {
        color: 'text-green-600',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        icon: <PartyPopper className="w-5 h-5" />,
        label: t("served")
      },
      'cancelled': {
        color: 'text-red-600',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        icon: <XCircle className="w-5 h-5" />,
        label: t("cancelled")
      },
    };
    return configs[status] || configs['placed'];
  };

  const getStatusSteps = () => {
    const steps = ['placed', 'confirmed', 'preparing', 'ready', 'served'];
    const currentIndex = steps.indexOf(order?.status || 'placed');
    return steps.map((step, index) => ({
      ...getStatusConfig(step),
      step,
      isCompleted: index <= currentIndex,
      isCurrent: index === currentIndex,
    }));
  };

  // Star Rating Component
  const StarRating = ({ rating, onRate, size = 'md' }: { rating: number; onRate: (r: number) => void; size?: 'sm' | 'md' | 'lg' }) => {
    const sizeClasses = {
      sm: 'w-5 h-5',
      md: 'w-7 h-7',
      lg: 'w-9 h-9',
    };
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRate(star)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <Star
              className={`${sizeClasses[size]} ${star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300 dark:text-gray-600'
                }`}
            />
          </button>
        ))}
      </div>
    );
  };


  // Session ended screen
  if (sessionEnded) {
    const qrCode = tableQrCode || order?.table?.qrCode;
    return (
      <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {t("thankYouForVisit")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t("sessionEndedMessage")}
          </p>
          <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-4 mb-6">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("redirectingIn")} <span className="font-bold text-red-600">{redirectCountdown}</span> {t("seconds")}
            </p>
          </div>
          <Button
            onClick={() => {
              if (qrCode) {
                navigate(`/table/${qrCode}`);
              } else {
                navigate('/');
              }
            }}
            className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-12"
          >
            {t("startNewOrder")}
          </Button>
        </div>
      </div>
    );
  }

  // Manual code entry screen
  if (!confirmationCode && !order) {
    return (
      <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-sm w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t("checkOrderStatus")}</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2">{t("enterConfirmationCode")}</p>
          </div>

          <form onSubmit={handleManualSubmit} className="space-y-4">
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder={t("confirmationCodePlaceholder")}
              className="text-center text-lg font-mono tracking-widest rounded-xl h-14"
              maxLength={6}
            />
            <Button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-12"
              disabled={loading || !manualCode.trim()}
            >
              {loading ? t("checking") : t("checkStatus")}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading && !order) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t("loadingOrderStatus")}</p>
        </div>
      </div>
    );
  }

  // Order not found
  if (!order) {
    return (
      <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t("orderNotFound")}</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{t("orderNotFoundDescription")}</p>
          <Button onClick={() => navigate('/')} className="w-full">
            {t("goHome")}
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(order.status);
  const restaurantName = language === 'ar' && order.restaurant.nameAr ? order.restaurant.nameAr : order.restaurant.name;


  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white dark:bg-gray-800 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            {order.restaurant.logo ? (
              <img
                src={order.restaurant.logo}
                alt={restaurantName}
                className="w-10 h-10 rounded-xl object-cover border border-gray-200 dark:border-gray-600"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Utensils className="w-5 h-5 text-red-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                {restaurantName}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("table")} {order.table.tableNumber} • #{order.orderNumber}
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
            >
              <RefreshCw className={`w-5 h-5 text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-[72px] pb-32 px-4">
        {/* Status Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-4">
          <div className="text-center mb-6">
            <div className={`w-20 h-20 ${statusConfig.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <span className={statusConfig.color}>{statusConfig.icon}</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {statusConfig.label}
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              {order.status === 'preparing' && t("yourOrderIsBeingPrepared")}
              {order.status === 'ready' && t("yourOrderIsReady")}
              {order.status === 'served' && t("enjoyYourMeal")}
              {order.status === 'placed' && t("waitingForConfirmation")}
              {order.status === 'confirmed' && t("orderConfirmedPreparing")}
            </p>
          </div>

          {/* Progress Steps */}
          {order.status !== 'cancelled' && (
            <div className="flex items-center justify-between px-4">
              {getStatusSteps().slice(0, -1).map((step, index) => (
                <React.Fragment key={step.step}>
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step.isCompleted
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
                      }`}>
                      {step.isCompleted ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <span className="text-sm font-medium">{index + 1}</span>
                      )}
                    </div>
                    <span className={`text-xs mt-2 ${step.isCompleted ? 'text-green-600 font-medium' : 'text-gray-400'
                      }`}>
                      {step.label}
                    </span>
                  </div>
                  {index < 3 && (
                    <div className={`flex-1 h-1 mx-2 rounded ${step.isCompleted && getStatusSteps()[index + 1]?.isCompleted
                      ? 'bg-green-500'
                      : 'bg-gray-200 dark:bg-gray-700'
                      }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>


        {/* Rating Section - Show when served and not yet rated */}
        {order.status === 'served' && !hasRated && showRating && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-4 border-2 border-yellow-400">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-yellow-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                {t("howWasYourMeal")}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {t("ratingHelpsUsImprove")}
              </p>
            </div>

            {/* Overall Rating */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("overallExperience")}
              </label>
              <div className="flex justify-center">
                <StarRating rating={overallRating} onRate={setOverallRating} size="lg" />
              </div>
            </div>

            {/* Service Rating */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("serviceQuality")}
              </label>
              <div className="flex justify-center">
                <StarRating rating={serviceRating} onRate={setServiceRating} size="md" />
              </div>
            </div>

            {/* Item Ratings */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {t("rateYourItems")} <span className="text-gray-400 font-normal">({t("optional")})</span>
              </label>
              <div className="space-y-3">
                {order.items.map((item, index) => {
                  const itemName = language === 'ar' && item.foodItem.nameAr ? item.foodItem.nameAr : item.foodItem.name;
                  const itemRating = itemRatings.find(ir => ir.foodItemId === item.foodItem._id);
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center text-xs font-medium">
                          {item.quantity}x
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{itemName}</span>
                      </div>
                      <StarRating
                        rating={itemRating?.rating || 0}
                        onRate={(r) => handleItemRatingChange(item.foodItem._id, r)}
                        size="sm"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Comment */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("additionalComments")} <span className="text-gray-400 font-normal">({t("optional")})</span>
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("shareYourThoughts")}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                rows={3}
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowRating(false)}
                variant="outline"
                className="flex-1 rounded-xl h-12"
              >
                {t("maybeLater")}
              </Button>
              <Button
                onClick={handleSubmitRating}
                disabled={submittingRating || overallRating === 0}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl h-12"
              >
                {submittingRating ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t("submitting")}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    {t("submitRating")}
                  </span>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Already Rated Message */}
        {(hasRated || order.rating?.ratedAt) && (
          <div className="bg-green-50 dark:bg-green-900/20 rounded-2xl p-4 mb-4 border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800 dark:text-green-300">{t("thankYouForRating")}</p>
                <p className="text-sm text-green-600 dark:text-green-400">{t("yourFeedbackHelps")}</p>
              </div>
            </div>
          </div>
        )}


        {/* Order Details */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-red-600" />
            {t("orderItemsDetails")}
          </h3>

          <div className="space-y-3">
            {order.items.map((item, index) => {
              const itemName = language === 'ar' && item.foodItem.nameAr ? item.foodItem.nameAr : item.foodItem.name;
              const sizeName = item.size
                ? (language === 'ar' && item.size.nameAr ? item.size.nameAr : item.size.name)
                : '';
              return (
                <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300">
                      {item.quantity}x
                    </span>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{itemName}</p>
                      {sizeName && (
                        <p className="text-xs text-red-600 font-medium">{sizeName}</p>
                      )}
                      {item.specialInstructions && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{item.specialInstructions}</p>
                      )}
                    </div>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">SDG {(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-900 dark:text-white">{t("total")}</span>
              <span className="text-2xl font-bold text-red-600">SDG {order.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Order Info */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t("orderNumber")}</p>
              <p className="font-semibold text-gray-900 dark:text-white">#{order.orderNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t("confirmationCode")}</p>
              <p className="font-semibold text-gray-900 dark:text-white font-mono">{order.confirmationCode}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t("table")}</p>
              <p className="font-semibold text-gray-900 dark:text-white">{order.table.tableNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t("orderTime")}</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {new Date(order.createdAt).toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Receipt Modal */}
      {showReceipt && receiptData && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t("receipt")}</h2>
              <button
                onClick={() => setShowReceipt(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Receipt Content */}
            <div className="flex-1 overflow-y-auto p-4" ref={receiptRef}>
              {/* Thank You Message */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {t("thankYouForVisit")}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  {t("hopeToSeeYouAgain")}
                </p>
              </div>

              {/* Restaurant Info */}
              <div className="text-center mb-4 pb-4 border-b border-dashed border-gray-300 dark:border-gray-600">
                {receiptData.restaurant.logo && (
                  <img
                    src={receiptData.restaurant.logo}
                    alt={receiptData.restaurant.name}
                    className="w-12 h-12 rounded-lg mx-auto mb-2 object-cover"
                  />
                )}
                <h4 className="font-bold text-gray-900 dark:text-white">
                  {language === 'ar' && receiptData.restaurant.nameAr ? receiptData.restaurant.nameAr : receiptData.restaurant.name}
                </h4>
                {receiptData.restaurant.address && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {typeof receiptData.restaurant.address === 'string'
                      ? receiptData.restaurant.address
                      : [
                        receiptData.restaurant.address.street,
                        receiptData.restaurant.address.city,
                        receiptData.restaurant.address.state,
                        receiptData.restaurant.address.zipCode
                      ].filter(Boolean).join(', ')
                    }
                  </p>
                )}
                {receiptData.restaurant.phone && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{receiptData.restaurant.phone}</p>
                )}
              </div>

              {/* Table & Date */}
              <div className="flex justify-between text-sm mb-4 pb-4 border-b border-dashed border-gray-300 dark:border-gray-600">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t("table")}: </span>
                  <span className="font-medium text-gray-900 dark:text-white">{receiptData.table.tableNumber}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t("date")}: </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {new Date(receiptData.closedAt).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}
                  </span>
                </div>
              </div>

              {/* Orders */}
              {receiptData.orders.map((receiptOrder, orderIndex) => (
                <div key={orderIndex} className="mb-4 pb-4 border-b border-dashed border-gray-300 dark:border-gray-600 last:border-0">
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                    <span>{t("order")} #{receiptOrder.orderNumber}</span>
                    <span>
                      {new Date(receiptOrder.createdAt).toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  {receiptOrder.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex justify-between text-sm py-1">
                      <span className="text-gray-700 dark:text-gray-300">
                        {item.quantity}x {language === 'ar' && item.nameAr ? item.nameAr : item.name}
                      </span>
                      <span className="text-gray-900 dark:text-white font-medium">SDG {item.total.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}

              {/* Summary */}
              <div className="space-y-2 pt-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">{t("subtotal")}</span>
                  <span className="text-gray-900 dark:text-white">SDG {(receiptData.summary?.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">{t("tax")}</span>
                  <span className="text-gray-900 dark:text-white">SDG {(receiptData.summary?.tax || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-gray-900 dark:text-white">{t("total")}</span>
                  <span className="text-red-600">SDG {(receiptData.summary?.total || 0).toFixed(2)}</span>
                </div>
                <p className="text-xs text-center text-gray-400 dark:text-gray-500 mt-2">
                  {receiptData.summary?.orderCount || 0} {(receiptData.summary?.orderCount || 0) === 1 ? t("order") : t("orders")}
                </p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex gap-3">
                <Button
                  onClick={handleDownloadReceipt}
                  variant="outline"
                  className="flex-1 rounded-xl h-12"
                >
                  <Download className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t("downloadReceipt")}
                </Button>
                <Button
                  onClick={() => {
                    setShowReceipt(false);
                    navigate('/');
                  }}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl h-12"
                >
                  {t("done")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fixed Bottom Actions */}
      {!tableClosed && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="flex gap-3">
            {order.status === 'served' && !hasRated && !showRating && (
              <Button
                onClick={() => setShowRating(true)}
                variant="outline"
                className="flex-1 border-yellow-400 text-yellow-600 hover:bg-yellow-50 rounded-xl h-14"
              >
                <Star className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t("rateOrder")}
              </Button>
            )}
            {order.status === 'served' && (
              <Button
                onClick={handleCloseTable}
                disabled={closingTable}
                variant="outline"
                className="flex-1 border-gray-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl h-14"
              >
                {closingTable ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                    {t("closing")}
                  </span>
                ) : (
                  <>
                    <Receipt className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t("closeTable")}
                  </>
                )}
              </Button>
            )}
            <Button
              onClick={handleOrderMore}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl h-14 text-lg font-semibold"
            >
              <Plus className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t("orderMore")}
            </Button>
          </div>
          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-3">
            {t("autoRefreshNote")}
          </p>
        </div>
      )}
    </div>
  );
};

export default TableOrderStatusPage;